import React, { useState, useRef, useEffect } from 'react'
import map from 'lodash/map'
import filter from 'lodash/filter'
import WorkspaceSelectionRow from './WorkspaceSelectionRow'

import {
  IWorkspace,
} from '@SiteModules/Workspace/types'

import './WorkspaceSelectionTable.scss'

const TABLE_HEADER_HEIGHT = 40 // px, from mixing dashboard_table

interface WorkspaceSelectionTableProps {
  readonly workspaces: IWorkspace[]
  readonly selectedWorkspaceIds: number[]
  readonly showNumberOfProjects?: boolean
  readonly popupClassName: string
  readonly remainingPopupHeight: number
  setSelectedWorkspaceIds(newSelectedWorkspaceIds: number[]): void
}

const WorkspaceSelectionTable: React.FC<WorkspaceSelectionTableProps> = props => {
  const {
    workspaces,
    selectedWorkspaceIds,
    showNumberOfProjects,
    popupClassName,
    remainingPopupHeight,
  } = props

  const [hovering, setHovering] = useState(false)
  const [style, setStyle] = useState({})
  const checkboxRef = useRef<HTMLLabelElement | null>(null)
  const numberOfSelectedWorkspaces = selectedWorkspaceIds.length
  const allWorkspacesSelected = numberOfSelectedWorkspaces === workspaces.length

  useEffect(() => {
    if (allWorkspacesSelected) {
      checkboxRef.current?.style?.setProperty('visibility', 'visible')
    } else if (!hovering) {
      checkboxRef.current?.style?.setProperty('visibility', 'hidden')
    }
  }, [allWorkspacesSelected])

  useEffect(() => {
    const updateStyle = () => {
      const el = document.getElementsByClassName(popupClassName)[0] as HTMLElement | undefined
      if (!el) {
        return
      }

      const viewportHeight = window.innerHeight
      const elHeight = el.offsetHeight

      if (elHeight === viewportHeight) {
        setStyle({ height: `calc(100vh - ${remainingPopupHeight}px - ${TABLE_HEADER_HEIGHT}px)` })
      } else {
        setStyle({})
      }
    }

    updateStyle()
    window.addEventListener('resize', updateStyle)

    return () => {
      window.removeEventListener('resize', updateStyle)
    }
  }, [remainingPopupHeight])

  const onMouseEnter = () => {
    setHovering(true)
    checkboxRef.current?.style?.setProperty('visibility', 'visible')
  }

  const onMouseLeave = () => {
    setHovering(false)
    if (allWorkspacesSelected) { return }

    checkboxRef.current?.style?.setProperty('visibility', 'hidden')
  }

  const selectOrUnselectAllWorkspaces = () => {
    if (selectedWorkspaceIds.length === workspaces.length) {
      props.setSelectedWorkspaceIds([])
    } else {
      const allWorkspaceIds = map(workspaces, workspace => workspace.id)
      props.setSelectedWorkspaceIds(allWorkspaceIds)
    }
  }

  const changeSelectedWorkspaces = (e: React.ChangeEvent<HTMLInputElement>, workspaceId: number) => {
    if (e.target.checked) {
      props.setSelectedWorkspaceIds(selectedWorkspaceIds.concat(workspaceId))
    } else {
      props.setSelectedWorkspaceIds(filter(selectedWorkspaceIds, selectedWorkspaceId => selectedWorkspaceId !== workspaceId))
    }
  }

  const renderTable = () => {
    return (
      <table
        className="WorkspaceSelectionTable__table"
      >
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody
          className="WorkspaceSelectionTable__body"
          style={style}
        >
          {tableRows}
        </tbody>
      </table>
    )
  }

  const tableRows = map(workspaces, (workspace, index) => {
    const selected = selectedWorkspaceIds.includes(workspace.id)

    return (
      <WorkspaceSelectionRow
        key={index}
        index={index}
        workspace={workspace}
        changeSelectedWorkspaces={changeSelectedWorkspaces}
        selected={selected}
        showNumberOfProjects={showNumberOfProjects}
      />
    )
  })

  const renderTableHeader = () => {
    return (
      <tr
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <th className="WorkspaceSelectionTable__check">
          {renderCheckbox()}
        </th>
        <th className="WorkspaceSelectionTable__workspace">
          Workspace
        </th>
      </tr>
    )
  }

  const renderCheckbox = () => {
    return (
      <label className="WorkspaceSelectionTable__checkbox" ref={checkboxRef}>
        <input
          type="checkbox"
          onChange={selectOrUnselectAllWorkspaces}
          checked={allWorkspacesSelected}
        />
        <div className="WorkspaceSelectionTable__checkmark" />
      </label>
    )
  }

  return (
    <div className="WorkspaceSelectionTable">
      {renderTable()}
    </div>
  )
}

export default WorkspaceSelectionTable
